import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

// Testicle masses are { size1, size2, size3, echotexture, description } objects —
// must be formatted individually, not joined directly (that stringifies to
// "[object Object]").
function formatTesticleMass(mass) {
  const parts = [];
  const size = [mass.size1, mass.size2, mass.size3].filter(Boolean).join(' x ');
  if (size) parts.push(`${size} cm`);
  if (mass.echotexture) parts.push(mass.echotexture);
  if (mass.description) parts.push(mass.description);
  return parts.join(', ');
}

// Form's per-side testicle.exclusion ('' | 'Orchiectomy' | 'Not Evaluated').
// When set, that side's testicle AND epididymis are reported with this one
// status line instead of their (default "Normal") findings.
function exclusionText(exclusion) {
  if (exclusion === 'Orchiectomy') return 'Surgically absent (orchiectomy)';
  if (exclusion === 'Not Evaluated') return 'Not evaluated';
  return null;
}

function renderTesticleSection(title, t) {
  const excludedText = exclusionText(t.exclusion);
  if (excludedText) {
    return (
      <>
        <PdfAccentSectionHeader title={title} />
        <PdfLabelValue label="Status:" value={excludedText} isAbnormal={t.exclusion === 'Orchiectomy'} forceBlack={t.exclusion !== 'Orchiectomy'} />
      </>
    );
  }
  return (
    <>
      <PdfAccentSectionHeader title={title} />
      <PdfLabelValue label="Echotexture:" value={t.echotexture || ''} isAbnormal={t.echotexture && t.echotexture !== 'Normal'} />
      <PdfLabelValue label="Hydrocele:" value={t.hydrocele ? 'Present' : 'None'} isAbnormal={!!t.hydrocele} />
      <PdfLabelValue label="Varicocele:" value={t.varicocele ? 'Present' : 'None'} isAbnormal={!!t.varicocele} />
      <PdfLabelValue label="Vascularity:" value={t.vascularity || ''} isAbnormal={t.vascularity && t.vascularity !== 'Normal'} />
      {t.other && <PdfLabelValue label="Other:" value={t.other} isAbnormal />}
      {t.masses && t.masses.length > 0 && (
        <PdfLabelValue label="Masses:" value={t.masses.filter(Boolean).map(formatTesticleMass).join('; ')} isAbnormal />
      )}
    </>
  );
}

// Epididymis follows its testicle's exclusion (same side)
function renderEpididymisSection(title, e, testicleExclusion) {
  const excludedText = exclusionText(testicleExclusion);
  return (
    <>
      <PdfAccentSectionHeader title={title} />
      {excludedText ? (
        <PdfLabelValue label="Status:" value={excludedText} isAbnormal={testicleExclusion === 'Orchiectomy'} forceBlack={testicleExclusion !== 'Orchiectomy'} />
      ) : (
        <>
          <PdfLabelValue label="Status:" value={e.status || ''} isAbnormal={e.status && e.status !== 'Normal'} />
          {e.other && <PdfLabelValue label="Other:" value={e.other} isAbnormal />}
        </>
      )}
    </>
  );
}

// Renders one hernia entry. "No hernia visualized" and "Other" are not
// actually hernias, so they skip the "Hernia N:" numbering and the
// hernia-specific contents fields (fat/bowel/fluid) entirely.
function renderHerniaEntry(h, i) {
  if (h.findings === 'No hernia visualized') {
    return (
      <View key={i} style={{ marginBottom: 6 }}>
        <PdfLabelValue label="Findings:" value={h.findings} forceBlack />
        {h.other && <PdfLabelValue label="Other:" value={h.other} isAbnormal />}
      </View>
    );
  }
  if (h.findings === 'Other') {
    return (
      <View key={i} style={{ marginBottom: 6 }}>
        <PdfLabelValue label="Findings:" value="Other" isAbnormal />
        {h.maximumSize && <PdfLabelValue label="Maximum size:" value={`${h.maximumSize} cm`} isAbnormal />}
        {h.other && <PdfLabelValue label="Other:" value={h.other} isAbnormal />}
      </View>
    );
  }
  return (
    <View key={i} style={{ marginBottom: 6 }}>
      <Text style={[commonStyles.label, { marginBottom: 2 }]}>Hernia {i + 1}:</Text>
      {h.findings && <PdfLabelValue label="Findings:" value={h.findings} isAbnormal />}
      {h.maximumSize && <PdfLabelValue label="Maximum size:" value={`${h.maximumSize} cm`} isAbnormal />}
      <PdfLabelValue label="Contains fat:" value={h.containsFat ? 'Yes' : 'No'} isAbnormal={h.containsFat} />
      <PdfLabelValue label="Contains bowel:" value={h.containsBowel ? 'Yes' : 'No'} isAbnormal={h.containsBowel} />
      <PdfLabelValue label="Contains fluid:" value={h.containsFluid ? 'Yes' : 'No'} isAbnormal={h.containsFluid} />
      {h.other && <PdfLabelValue label="Other:" value={h.other} isAbnormal />}
    </View>
  );
}

// Renders one INGUINAL CANAL section (right or left) — no Location field,
// since which side it is is now implied by which section this is.
function renderInguinalCanalSection(title, canal) {
  const hernias = canal.hernias || [];
  return (
    <>
      <PdfAccentSectionHeader title={title} />
      {canal.evaluated ? (
        hernias.length === 0 ? (
          <PdfLabelValue label="Finding:" value="No hernia identified" forceBlack />
        ) : (
          hernias.map((h, i) => renderHerniaEntry(h, i))
        )
      ) : (
        <PdfLabelValue label="Status:" value="Not evaluated" forceBlack />
      )}
    </>
  );
}

export function ScrotalPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const rt = data.rightTesticle || {};
  const lt = data.leftTesticle || {};
  const re = data.rightEpididymis || {};
  const le = data.leftEpididymis || {};
  const ric = data.rightInguinalCanal || {};
  const lic = data.leftInguinalCanal || {};

  const rtVol = (rt.length && rt.width && rt.height)
    ? calculateVolume(rt.length, rt.width, rt.height) : null;
  const ltVol = (lt.length && lt.width && lt.height)
    ? calculateVolume(lt.length, lt.width, lt.height) : null;

  const measurementsText = [
    (!rt.exclusion && rt.length && rt.width && rt.height)
      ? `Right testicle: ${rt.length} x ${rt.width} x ${rt.height} cm${rtVol ? ` (Vol: ${rtVol} cc)` : ''}` : null,
    (!lt.exclusion && lt.length && lt.width && lt.height)
      ? `Left testicle: ${lt.length} x ${lt.width} x ${lt.height} cm${ltVol ? ` (Vol: ${ltVol} cc)` : ''}` : null,
  ].filter(Boolean).join('\n');

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="SCROTAL ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            {renderTesticleSection('RIGHT TESTICLE', rt)}
            {renderEpididymisSection('RIGHT EPIDIDYMIS', re, rt.exclusion)}
            {renderTesticleSection('LEFT TESTICLE', lt)}
            {renderEpididymisSection('LEFT EPIDIDYMIS', le, lt.exclusion)}

            {renderInguinalCanalSection('RIGHT INGUINAL CANAL', ric)}
            {renderInguinalCanalSection('LEFT INGUINAL CANAL', lic)}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
